import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { APP_ROUTES } from '@ps/core-config';

import { retryServerError } from '../../utils/retry-server-error.operator';
import { AuthService } from '../../services/auth.service';
import { SecureAuthStorageService } from '../../services/secure-auth-storage/secure-auth-storage.service';
import { AuthSessionActions } from './auth-session.actions';
import { selectUser } from './auth-session.selectors';

const RETRY = {
  count: 3,
  delay: 500,
};

@Injectable()
export class AuthSessionEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _authService = inject(AuthService);
  private readonly _authStorage = inject(SecureAuthStorageService);
  private readonly _router = inject(Router);

  public login$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.login),
      exhaustMap((credentials) =>
        this._authService.login(credentials).pipe(
          retryServerError(RETRY.count, RETRY.delay),
          map((user) => AuthSessionActions.loginSuccess({ user })),
          this.psCatchError(
            (error) =>
              of(AuthSessionActions.loginFailure({ error: error.message })),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loginNavigate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthSessionActions.loginSuccess),
        tap(() => {
          this._router.navigate([APP_ROUTES.DASHBOARD]);
        }),
      ),
    { dispatch: false },
  );

  public logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.logout),
      concatLatestFrom(() => this._store.select(selectUser)),
      exhaustMap(([, user]) =>
        Option.from(user).match({
          some: ({ id, isSso }) =>
            this._authService.logout(id).pipe(
              map(() =>
                AuthSessionActions.logoutSuccess({ isSso: isSso ?? false }),
              ),
              catchError((error) =>
                of(AuthSessionActions.logoutFailure({ error })),
              ),
            ),
          none: () => of(AuthSessionActions.logoutSuccess({ isSso: false })),
        }),
      ),
    ),
  );

  public logoutSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthSessionActions.logoutSuccess),
      map(() => AuthSessionActions.clearState()),
    ),
  );

  public clearState$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthSessionActions.clearState),
        tap(() => {
          this._authStorage.clear();
          this._router.navigate([APP_ROUTES.LOGIN]);
        }),
      ),
    { dispatch: false },
  );
}
